import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { MapPin, Calendar, Users, Sparkles, CheckCircle2, History } from "lucide-react";

function isUpcoming(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() >= today.getTime();
}

export default function Events() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events").then(r => setEvents(r.data)).catch(() => {});
  }, []);

  const { upcoming, closed } = useMemo(() => {
    const up = [];
    const cl = [];
    [...events].forEach(e => (isUpcoming(e.date) ? up : cl).push(e));
    up.sort((a, b) => new Date(a.date) - new Date(b.date)); // soonest first
    cl.sort((a, b) => new Date(b.date) - new Date(a.date)); // most recent past first
    return { upcoming: up, closed: cl };
  }, [events]);

  const register = async (id) => {
    if (!user || typeof user !== "object") { toast.error("Login required"); return; }
    try { await api.post(`/events/${id}/register`); toast.success("Registered"); }
    catch { toast.error("Could not register"); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-10">
            <div className="text-xs uppercase tracking-widest text-brand-green font-semibold">Events</div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Training, workshops & conferences</h1>
          </div>

          {/* Upcoming */}
          <section className="mb-14">
            <div className="flex items-end justify-between mb-5">
              <div>
                <Badge className="bg-brand-blue/10 text-brand-blue border-brand-blue/30 mb-2">
                  <Sparkles className="w-3 h-3 mr-1" /> Upcoming
                </Badge>
                <h2 className="text-2xl font-black tracking-tight">Open for registration</h2>
              </div>
              <div className="text-xs text-muted-foreground">{upcoming.length} event{upcoming.length !== 1 ? "s" : ""}</div>
            </div>
            {upcoming.length === 0 ? (
              <Card className="p-10 text-center text-sm text-muted-foreground border-dashed">
                No upcoming events scheduled right now. Check back soon.
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {upcoming.map(e => (
                  <EventCard key={e.id} ev={e} variant="upcoming" onRegister={register} />
                ))}
              </div>
            )}
          </section>

          {/* Closed */}
          {closed.length > 0 && (
            <section>
              <div className="flex items-end justify-between mb-5">
                <div>
                  <Badge variant="outline" className="mb-2 text-muted-foreground">
                    <History className="w-3 h-3 mr-1" /> Closed
                  </Badge>
                  <h2 className="text-2xl font-black tracking-tight">Past events</h2>
                </div>
                <div className="text-xs text-muted-foreground">{closed.length} event{closed.length !== 1 ? "s" : ""}</div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {closed.map(e => (
                  <EventCard key={e.id} ev={e} variant="closed" onRegister={register} />
                ))}
              </div>
            </section>
          )}
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}

function EventCard({ ev, variant, onRegister }) {
  const isUp = variant === "upcoming";
  return (
    <Card
      className={
        isUp
          ? "p-5 hover-lift border-2 border-brand-blue/40 ring-1 ring-brand-blue/10 bg-card relative overflow-hidden"
          : "p-5 border-border bg-muted/30 relative opacity-90"
      }
      data-testid={`event-card-${ev.id}`}
    >
      {isUp && (
        <div className="absolute top-0 right-0 px-3 py-1 text-[10px] uppercase tracking-wider font-bold bg-brand-blue text-white rounded-bl-md">
          Upcoming
        </div>
      )}
      {!isUp && (
        <div className="absolute top-0 right-0 px-3 py-1 text-[10px] uppercase tracking-wider font-bold bg-muted-foreground/80 text-white rounded-bl-md flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> Closed
        </div>
      )}

      <div className="flex items-center gap-2 mt-1 mb-1">
        <Badge variant="outline" className="text-[10px]">{ev.type}</Badge>
        <span className="text-xs text-muted-foreground">{ev.date}</span>
      </div>
      <h3 className={`font-bold text-lg mt-2 ${!isUp ? "text-muted-foreground" : ""}`}>{ev.title}</h3>
      <p className={`text-sm mt-1 ${isUp ? "text-muted-foreground" : "text-muted-foreground/70"}`}>{ev.description}</p>
      <div className="text-xs text-muted-foreground mt-3 space-y-1">
        <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {ev.date}</div>
        <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {ev.location}</div>
        <div className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {ev.seats} seats</div>
      </div>
      {isUp ? (
        <Button
          onClick={() => onRegister(ev.id)}
          className="mt-4 w-full bg-brand-blue text-white hover:bg-brand-blue/90"
          data-testid={`event-register-${ev.id}`}
        >
          Register
        </Button>
      ) : (
        <Button disabled className="mt-4 w-full" variant="outline">
          Registration closed
        </Button>
      )}
    </Card>
  );
}
